import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useTranslation from "../../../shared/hooks/useTranslation";
import colors from "../../../shared/themes/colors";
import { StatusChip } from "../../../shared/components/chips/StatusChip";
import { ReceivedOffer, OfferStatusCode } from "../models/receivedOffersModels";
import { CompletionTimeCode, WarrantyPeriodCode } from "../../serviceOffers/models/models";
import {
  createReceivedOfferStatusLabelMap,
  createReceivedOfferStatusColorMap,
} from "../utils/receivedOfferStatusMaps";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import RejectOfferDialog from "./RejectOfferDialog";
import { dt } from "../../../shared/themes/redesignTokens";
import { getUiLocale } from "../../../shared/utils/relativeTime";

interface OfferComparisonTableProps {
  offers: ReceivedOffer[];
  onAccept: (offerId: string) => void;
  onReject: (offerId: string, reason?: string) => void;
  actionLoading: boolean;
}

const includedServiceKeys: { key: keyof ReceivedOffer; translationKey: string }[] = [
  { key: "includeDiagnostics", translationKey: "serviceOffers:includeDiagnostics" },
  { key: "includeLaborForRepair", translationKey: "serviceOffers:includeLaborForRepair" },
  { key: "includePartsAndMaterials", translationKey: "serviceOffers:includePartsAndMaterials" },
  { key: "includeTestingAndCalibration", translationKey: "serviceOffers:includeTestingAndCalibration" },
  { key: "includeWorkAreaCleanup", translationKey: "serviceOffers:includeWorkAreaCleanup" },
  { key: "includeDetailedServiceReport", translationKey: "serviceOffers:includeDetailedServiceReport" },
];

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return "-";
  return `${value.toLocaleString(getUiLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}\u20AC`;
};

const canTakeAction = (status: OfferStatusCode) => {
  return status === OfferStatusCode.sent || status === OfferStatusCode.viewed;
};

const OfferComparisonTable = ({
  offers,
  onAccept,
  onReject,
  actionLoading,
}: OfferComparisonTableProps) => {
  const { translate } = useTranslation(["servicing", "serviceOffers", "common"]);
  const confirm = useConfirmation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuOffer, setMenuOffer] = useState<ReceivedOffer | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectOffer, setRejectOffer] = useState<ReceivedOffer | null>(null);

  const formatCompletionTime = (code?: CompletionTimeCode, custom?: string) => {
    if (!code) return "-";
    if (code === CompletionTimeCode.custom) return custom || "-";
    return translate(`serviceOffers:completionTimeCodes.${CompletionTimeCode[code]}`);
  };

  const formatWarranty = (code?: WarrantyPeriodCode, custom?: string) => {
    if (!code) return "-";
    if (code === WarrantyPeriodCode.custom) return custom || "-";
    return translate(`serviceOffers:warrantyPeriodCodes.${WarrantyPeriodCode[code]}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, offer: ReceivedOffer) => {
    setMenuAnchor(event.currentTarget);
    setMenuOffer(offer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuOffer(null);
  };

  const handleAccept = (offer: ReceivedOffer) => {
    handleMenuClose();
    confirm({
      title: translate("servicing:acceptOfferTitle"),
      text: translate("servicing:acceptOfferText", { number: offer.internalNumber }),
      confirmButton: translate("servicing:yesAccept"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: () => onAccept(offer.id),
    });
  };

  const handleRejectOpen = (offer: ReceivedOffer) => {
    handleMenuClose();
    setRejectOffer(offer);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = (reason?: string) => {
    if (rejectOffer) {
      onReject(rejectOffer.id, reason);
    }
    setRejectDialogOpen(false);
    setRejectOffer(null);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          border: `1px solid ${colors.secondary.gray94}`,
          borderRadius: "8px",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: colors.primary.white1,
                  borderBottom: `1px solid ${colors.secondary.gray90}`,
                  color: colors.secondary.gray40,
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  py: 1.5,
                },
              }}
            >
              <TableCell>{translate("servicing:provider")}</TableCell>
              <TableCell>{translate("servicing:price")}</TableCell>
              <TableCell>{translate("servicing:includes")}</TableCell>
              <TableCell>{translate("servicing:timeline")}</TableCell>
              <TableCell>{translate("servicing:warranty")}</TableCell>
              <TableCell>{translate("servicing:status")}</TableCell>
              <TableCell align="center">{translate("servicing:actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.map((offer) => (
              <TableRow
                key={offer.id}
                sx={{
                  "& td": { borderBottom: `1px solid ${colors.secondary.gray94}`, py: 2 },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                {/* Provider */}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {offer.provider.logoUrl ? (
                      <Box
                        component="img"
                        src={offer.provider.logoUrl}
                        alt={offer.provider.name}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          backgroundColor: colors.primary.blue2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box className="text-12-bold" sx={{ color: dt.blue }}>
                          {offer.provider.name?.charAt(0)?.toUpperCase() || "?"}
                        </Box>
                      </Box>
                    )}
                    <Box>
                      <Box className="text-14-bold" sx={{ color: colors.secondary.gray15 }}>
                        {offer.provider.name}
                      </Box>
                    </Box>
                  </Box>
                </TableCell>

                {/* Price */}
                <TableCell>
                  <Box className="text-14-bold" sx={{ color: colors.secondary.gray15 }}>
                    {formatCurrency(offer.totalEstimatedCost)}
                  </Box>
                </TableCell>

                {/* Includes */}
                <TableCell>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    {includedServiceKeys.map((service) => (
                      <Box
                        key={service.key}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {offer[service.key] ? (
                          <CheckIcon sx={{ fontSize: 16, color: colors.primary.green }} />
                        ) : (
                          <CloseIcon sx={{ fontSize: 16, color: colors.primary.red }} />
                        )}
                        <Box className="text-12-regular" sx={{ color: colors.secondary.gray30 }}>
                          {translate(service.translationKey)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </TableCell>

                {/* Timeline */}
                <TableCell>
                  <Box className="text-14-regular" sx={{ color: colors.secondary.gray20 }}>
                    {formatCompletionTime(offer.completionTimeCode, offer.customCompletionTime)}
                  </Box>
                </TableCell>

                {/* Warranty */}
                <TableCell>
                  <Box className="text-14-regular" sx={{ color: colors.secondary.gray20 }}>
                    {formatWarranty(offer.warrantyPeriodCode, offer.customWarrantyPeriod)}
                  </Box>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <StatusChip
                    status={offer.statusCode}
                    colorMap={createReceivedOfferStatusColorMap()}
                    labelMap={createReceivedOfferStatusLabelMap(translate)}
                  />
                </TableCell>

                {/* Actions */}
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, offer)}
                    disabled={actionLoading}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuOffer && canTakeAction(menuOffer.statusCode) && [
          <MenuItem key="accept" onClick={() => handleAccept(menuOffer)}>
            {translate("servicing:acceptOffer")}
          </MenuItem>,
          <MenuItem key="reject" onClick={() => handleRejectOpen(menuOffer)}>
            {translate("servicing:rejectOffer")}
          </MenuItem>,
        ]}
      </Menu>

      {/* Reject Dialog */}
      <RejectOfferDialog
        open={rejectDialogOpen}
        offerNumber={rejectOffer?.internalNumber || ""}
        onConfirm={handleRejectConfirm}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectOffer(null);
        }}
      />
    </>
  );
};

export default OfferComparisonTable;
